import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { TbEdit, TbTrash } from 'react-icons/tb';
import { twMerge } from 'tailwind-merge';
import { RootState } from '../../../redux/reducers';
import { formatDate } from '../../../utils/stringUtils';
import {
  ContributionPlanWithMembersModel,
  BankMutation,
  JournalEntryModel,
  JOURNAL_ENTRY_SOURCE_TYPES,
  CreateJournalEntryDto,
  Currency,
  JournalEntrySourceType,
  LedgerAccount,
  LedgerAccountType,
  CreateJournalEntryLineDto,
  BankTransactionParticipation,
} from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/community/bankAccountService';
import Button from '../buttons/Button';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import ModalLayout from '../layouts/ModalLayout';
import Labeled from '../misc/Labeled';
import Avatar from '../misc/Avatar';

import DropdownSelect from '../forms/DropdownSelect';
import {
  formatEuroCentsWithSign,
  matchMutationsToConnections,
  calculateDisplayAmount,
  buildJournalEntryLines,
} from '../../../utils/bankAccountUtil';
import CurrencyInput from '../forms/CurrencyInput';
import SplitMembersList from '../financial/SplitMembersList';

import Slider from '../misc/Slider';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';
import TransactionProcessor from '../financial/TransactionProcessor';

// TypeBadge component
function TypeBadge({ sourceType }: { sourceType?: JournalEntrySourceType }) {
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });
  if (!sourceType) return null;
  const isIncome =
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME ||
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
    sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN;

  const typeLabel = tEnum(`JournalEntrySourceType.${sourceType}`);

  return (
    <span
      className={twMerge(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        isIncome
          ? 'bg-primary-300 bg-opacity-20 text-primary-900'
          : 'bg-secondary bg-opacity-20 text-primary-900',
      )}>
      {typeLabel}
    </span>
  );
}
/** Split row for create flow (no id) or edit flow (with journalEntryId for update) */
export type SplitRow = Omit<Partial<CreateJournalEntryDto>, 'entryDate'> & { 
  entryDate?: Date | string; // Can be Date object or ISO string
  journalEntryId?: number;
};

interface SplitModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialJournalEntry?: JournalEntryModel;
  /** When editing an existing split, pass all sibling journal entries (same mutationId) to prefill SPLIT mode */
  initialSplitJournalEntries?: JournalEntryModel[];
  /** Target amount the split rows must sum to (e.g. mutation amount). When omitted, uses mutation?.amount or sum of initialSplitJournalEntries. */
  initialSplitTargetAmount?: number;
  mutation?: BankMutation;
  onSplitSuccess?: (
    entry: CreateJournalEntryDto | SplitRow[],
  ) => void;
  initialProcessType?: ProcessType;
  initialInternalSide?: InternalSide;
  onInternalCreate?: (params: {
    fromMutationId: number;
    toMutationId: number;
    description?: string;
  }) => void;
}

type ProcessType = 'ASSIGN' | 'SPLIT' | 'INTERNAL';
type AssignmentType = 'ENTIRE_GROUP' | 'INDIVIDUALS';
type ContributionType = 'YES' | 'NO';
type InternalSide = 'FROM' | 'TO';

type JournalEntryState = {
  description: string;
  entryDate: Date;
  amount: number;
  sourceType?: JournalEntrySourceType;
  payerId?: number;
  mutationId?: number;
  selectedMembers: number[];
  memberValues: Record<number, number>;
  memberRations?: Record<number, number | undefined>;
  contributionPayment?: {
    contributionPlanId?: number;
    dashboardMemberId?: number;
  };
  processType?: ProcessType;
  assignmentType?: AssignmentType;
  isContributionPayment?: ContributionType;
  // Split functionality state (optional journalEntryId for edit-existing-split)
  splitJournalEntries?: SplitRow[];
  splitStep?: 'details';
  // Internal transfer state
  internalTransferSide?: InternalSide;
  internalCandidates?: BankMutation[];
  internalSelectedCounterpartId?: number;
};

const getEmptyJournalEntryState = (
  mutation?: BankMutation,
  initialEntry?: JournalEntryModel,
) => {
  const amount = mutation?.amount ?? (initialEntry ? calculateDisplayAmount(initialEntry) : 0);
  return {
    description: mutation?.description ?? initialEntry?.description ?? '',
    entryDate: mutation ? new Date(mutation.transactionDate) : (initialEntry ? new Date(initialEntry.entryDate) : new Date()),
    amount,
    mutationId: mutation?.mutationId ?? initialEntry?.mutationId ?? undefined,
    selectedMembers: [],
    memberValues: {},
    memberRations: {},
    assignmentType: undefined,
    isContributionPayment: undefined,
    processType: 'ASSIGN' as ProcessType, // Auto-select ASSIGN
  };
};

const transformJournalEntryIntoState = (entry: JournalEntryModel): JournalEntryState => {
  const netAmount = calculateDisplayAmount(entry);
  
  // Determine assignment type and contribution type based on source type
  let assignmentType: AssignmentType | undefined;
  let isContributionPayment: ContributionType | undefined;

  if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME || 
      entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE) {
    assignmentType = 'ENTIRE_GROUP';
  } else if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME || 
             entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE) {
    assignmentType = 'INDIVIDUALS';
    isContributionPayment = 'NO';
  } else if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN) {
    assignmentType = 'INDIVIDUALS';
    isContributionPayment = 'YES';
  }

  // If we have contribution payment data, override the assignment type
  if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && entry.dashboardMemberId) {
    assignmentType = 'INDIVIDUALS';
    isContributionPayment = 'YES';
  }

  // Calculate member rations from existing journal entry lines
  const calculateMemberRations = () => {
    if (!entry.lines || entry.lines.length === 0) return {};

    const memberRations: Record<number, number | undefined> = {};
    const totalAmount = Math.abs(netAmount);

    // Get member lines
    const memberLines = entry.lines.filter((line) => line.participantMemberId);
    if (memberLines.length === 0) return {};

    // Check if amounts are proportional (within 1 cent tolerance)
    const amounts = memberLines.map((line) => Math.abs(line.debit - line.credit));
    const totalParticipationAmount = amounts.reduce((sum, amount) => sum + amount, 0);

    // Only calculate rations if the total participation amount matches the transaction amount
    if (Math.abs(totalParticipationAmount - totalAmount) <= 1) {
      // Find the minimum amount to use as the base unit
      const minAmount = Math.min(...amounts);

      // Calculate rations based on the minimum amount
      memberLines.forEach((line) => {
        if (!line.participantMemberId) return;
        const amount = Math.abs(line.debit - line.credit);
        const ration = amount / minAmount;

        // Round to the nearest whole number if it's close enough
        const roundedRation = Math.round(ration);
        const tolerance = 0.1; // 10% tolerance for rounding

        if (Math.abs(ration - roundedRation) <= tolerance) {
          memberRations[line.participantMemberId] = roundedRation;
        } else {
          memberRations[line.participantMemberId] = undefined;
        }
      });
    }

    return memberRations;
  };

  // Extract member values from journal entry lines
  const memberValues: Record<number, number> = {};
  entry.lines?.forEach((line) => {
    if (line.participantMemberId) {
      const existing = memberValues[line.participantMemberId] || 0;
      memberValues[line.participantMemberId] = existing + (line.debit - line.credit);
    }
  });

  return {
    description: entry.description,
    entryDate: new Date(entry.entryDate),
    amount: netAmount,
    sourceType: entry.sourceType,
    payerId: entry.payerId ?? undefined,
    mutationId: entry.mutationId ?? undefined,
    selectedMembers: entry.lines?.filter((l) => l.participantMemberId).map((l) => l.participantMemberId!) ?? [],
    memberValues,
    memberRations: calculateMemberRations(),
    contributionPayment: entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && 
      entry.dashboardMemberId && entry.sourceId
      ? {
          dashboardMemberId: entry.dashboardMemberId,
          contributionPlanId: entry.sourceId as number,
        }
      : undefined,
    assignmentType,
    isContributionPayment,
    processType: 'ASSIGN' as ProcessType,
  };
};

export default function TransactionModal({
  open,
  setOpen,
  onSplitSuccess,
  onInternalCreate,
  initialJournalEntry,
  initialSplitJournalEntries,
  initialSplitTargetAmount,
  mutation,
  initialProcessType,
  initialInternalSide,
}: SplitModalProps) {
  const { t, i18n } = useTranslation('', { keyPrefix: 'page.financial.splitModal' });
  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER);
  const dashboardId = useSelector((state: RootState) => state.application.selectedDashboard);
  
  const [entry, setEntry] = useState<JournalEntryState>(() => {
    if (initialJournalEntry) {
      return transformJournalEntryIntoState(initialJournalEntry);
    }
    return getEmptyJournalEntryState(mutation);
  });
  
  const [remainingAmount, setRemainingAmount] = useState(() => {
    if (initialJournalEntry) {
      return calculateDisplayAmount(initialJournalEntry);
    }
    return mutation?.amount ?? 0;
  });
  const [plans, setPlans] = useState<Record<string, ContributionPlanWithMembersModel>>({});
  const [bankAccountsCount, setBankAccountsCount] = useState<number | undefined>(undefined);
  const [isViewMode, setIsViewMode] = useState(false);
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccount[]>([]);

  // Note: connectionsWithPlans and getContributionPlansForConnection removed - contribution payment selection UI removed

  // Note: Contribution payment selection removed - now handled by contribution runs

  const editMode = useMemo(() => !!initialJournalEntry, [initialJournalEntry]);
  const isExistingEntry = useMemo(
    () => !!initialJournalEntry?.journalEntryId,
    [initialJournalEntry],
  );
  const community = useSelectedCommunity();

  // Fetch ledger accounts when modal opens
  useEffect(() => {
    if (open && dashboardId) {
      bankAccountService.getWalletAccounts()
        .then((accounts: LedgerAccount[]) => {
          setLedgerAccounts(accounts);
        })
        .catch((error: unknown) => {
          console.error('Error fetching ledger accounts:', error);
          // Continue without ledger accounts - validation will catch this
        });
      
      // Fetch bank accounts count to determine if INTERNAL option should be shown
      bankAccountService.getAllBankAccounts()
        .then((accounts) => setBankAccountsCount(accounts.length))
        .catch((error: unknown) => {
          console.error('Error fetching bank accounts:', error);
          setBankAccountsCount(0);
        });
    }
  }, [open, dashboardId]);

  // Set view mode when opening existing journal entry
  useEffect(() => {
    if (open && isExistingEntry) {
      setIsViewMode(true);
    } else if (open && !isExistingEntry) {
      setIsViewMode(false);
    }
  }, [open, isExistingEntry]);

  useEffect(() => {
    bankAccountService.getAllContributionPlans().then((ps) => {
      setPlans(_.keyBy(ps, (p) => p.contributionPlanId));
    });
  }, [community?.id]);

  useEffect(() => {
    if (!initialJournalEntry) {
      setEntry(getEmptyJournalEntryState(mutation));
      setRemainingAmount(mutation?.amount ?? 0);
    } else {
      const base = transformJournalEntryIntoState(initialJournalEntry);
      if (
        initialSplitJournalEntries &&
        initialSplitJournalEntries.length > 0
      ) {
        // Convert journal entries to split rows
        const splitRows: SplitRow[] = initialSplitJournalEntries.map((journalEntry) => ({
          description: journalEntry.description,
          entryDate: typeof journalEntry.entryDate === 'string' ? journalEntry.entryDate : new Date(journalEntry.entryDate).toISOString(),
          sourceType: journalEntry.sourceType,
          mutationId: journalEntry.mutationId,
          lines: journalEntry.lines?.map((line) => ({
            ledgerAccountId: line.ledgerAccountId,
            participantMemberId: line.participantMemberId ?? undefined,
            debit: line.debit,
            credit: line.credit,
            currency: line.currency,
          })),
          journalEntryId: journalEntry.journalEntryId,
        }));
        // Target amount = the mutation (bank line) we're editing, not the individual entry amount
        // When editing an existing split, base.amount is just one split row, not the total
        const targetAmount =
          initialSplitTargetAmount ??
          mutation?.amount ??
          splitRows.reduce((sum, r) => {
            if (r.lines) {
              // Only sourceType and lines are needed by calculateDisplayAmount
              const fakeEntry = {
                sourceType: base.sourceType ?? JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE,
                lines: r.lines,
              } as unknown as JournalEntryModel;
              return sum + calculateDisplayAmount(fakeEntry);
            }
            return sum;
          }, 0);
        setEntry({
          ...base,
          processType: 'SPLIT',
          splitJournalEntries: splitRows,
          amount: targetAmount,
          splitStep: 'details',
        });
        setRemainingAmount(targetAmount);
      } else {
        setEntry(base);
        setRemainingAmount(calculateDisplayAmount(initialJournalEntry));
      }
    }
  }, [initialJournalEntry, mutation, initialSplitJournalEntries, initialSplitTargetAmount]);

  // Set transaction amount from mutation or initial transaction
  useEffect(() => {
    // Avoid conflicting with initialTransaction bootstrap effect
    if (initialJournalEntry) return;
    const transactionInfo = mutation || initialJournalEntry;
    if (transactionInfo) {
      setEntry((prevEntry) => {
        const desired = transactionInfo.amount;
        if (prevEntry.amount === desired) return prevEntry;
        return {
          ...prevEntry,
          amount: desired,
        };
      });
    }
  }, [mutation, initialJournalEntry]);

  // Set initial assignment type and contribution type when amount changes (for new entries)
  useEffect(() => {
    if (initialJournalEntry) return; // only for new entries
    if (entry.amount !== 0) {
      const isExpense = entry.amount < 0;

      // Set default assignment type based on amount
      if (!entry.assignmentType) {
        setEntry((prev) => ({
          ...prev,
          assignmentType: 'ENTIRE_GROUP',
        }));
      }

      // Set default source type based on assignment type
      if (entry.assignmentType && !entry.sourceType) {
        const newSourceType =
          entry.assignmentType === 'ENTIRE_GROUP'
            ? isExpense
              ? JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE
              : JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME
            : isExpense
              ? JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE
              : JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME;

        setEntry((prev) => {
          if (prev.sourceType === newSourceType) return prev;
          return {
            ...prev,
            sourceType: newSourceType,
          };
        });
      }
    }
  }, [entry.amount, entry.assignmentType, initialJournalEntry]);

  useEffect(() => {
    if (
      entry.sourceType &&
      (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
        entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE ||
        entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN)
    ) {
      setEntry((prev) => {
        if (
          prev.selectedMembers.length === 0 &&
          Object.keys(prev.memberValues).length === 0
        ) {
          return prev;
        }
        return {
          ...prev,
          selectedMembers: [],
          memberValues: {},
        };
      });
      setRemainingAmount(0);
    }
  }, [entry.sourceType]);

  // Set initial assignment type and contribution type based on source type
  useEffect(() => {
    if (entry.sourceType) {
      if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
          entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE) {
        setEntry((prev) => ({
          ...prev,
          assignmentType: 'ENTIRE_GROUP',
        }));
      } else if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME ||
                 entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE) {
        setEntry((prev) => ({
          ...prev,
          assignmentType: 'INDIVIDUALS',
        }));
      } else if (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN) {
        // Keep existing contribution run entries for viewing/editing, but don't allow creating new ones
        setEntry((prev) => ({
          ...prev,
          assignmentType: 'INDIVIDUALS',
        }));
      }
    }
  }, [entry.sourceType, entry.amount]);

  // Auto-match income entries to connections and contribution plans
  useEffect(() => {
    // Only auto-match when we haven't chosen an assignment yet
    if (entry.assignmentType) return;
    const hasContributionPayment = initialJournalEntry?.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN;
      
    if (
      mutation &&
      entry.amount > 0 &&
      !hasContributionPayment
    ) {
      // Try to match the mutation to a connection
      const matches = matchMutationsToConnections([mutation], connections);
      const match = matches[0];

      if (match.matchedConnection) {
        // Found a matching connection, set up for individual assignment
        const desiredId = +match.matchedConnection!.dashboardMemberId;
        setEntry((prev) => {
          const alreadySelected =
            prev.selectedMembers.length === 1 && prev.selectedMembers[0] === desiredId;
          const alreadyType = prev.assignmentType === 'INDIVIDUALS';
          const alreadyHasValue = prev.memberValues[desiredId] === prev.amount;
          if (alreadySelected && alreadyType && alreadyHasValue) return prev;
          
          // Pre-fill the amount for the matched member so they can immediately assign
          return {
            ...prev,
            assignmentType: 'INDIVIDUALS',
            selectedMembers: [desiredId],
            sourceType: JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME,
            memberValues: {
              [desiredId]: prev.amount,
            },
          };
        });
      } else {
        // No matching connection found, default to entire group
        setEntry((prev) => {
          if (prev.assignmentType === 'ENTIRE_GROUP') return prev;
          return {
            ...prev,
            assignmentType: 'ENTIRE_GROUP',
          };
        });
      }
    }
  }, [
    mutation,
    entry.amount,
    connections,
    plans,
    initialJournalEntry,
    entry.assignmentType,
  ]);

  // Auto-select ENTIRE_GROUP for expenses
  useEffect(() => {
    // Only set default ENTIRE_GROUP for expenses if not chosen yet
    if (entry.assignmentType) return;
    const hasContributionPaymentForExpense = initialJournalEntry?.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN;
    
    if (
      mutation &&
      entry.amount < 0 &&
      !hasContributionPaymentForExpense
    ) {
      setEntry((prev) => ({
        ...prev,
        assignmentType: 'ENTIRE_GROUP',
      }));
    }
  }, [mutation, entry.amount, initialJournalEntry, entry.assignmentType]);

  // Ensure processType is always set to ASSIGN
  useEffect(() => {
    if (!entry.processType) {
      setEntry((prev) => ({
        ...prev,
        processType: initialProcessType ?? 'ASSIGN',
      }));
    }
  }, [entry.processType, initialProcessType]);

  // Reset to ASSIGN if INTERNAL is selected but there's only 1 bank account
  useEffect(() => {
    if (entry.processType === 'INTERNAL' && bankAccountsCount !== undefined && bankAccountsCount <= 1) {
      setEntry((prev) => ({
        ...prev,
        processType: 'ASSIGN',
      }));
    }
  }, [entry.processType, bankAccountsCount]);

  // Initialize internal side if provided
  useEffect(() => {
    if (initialInternalSide && entry.internalTransferSide !== initialInternalSide) {
      setEntry((prev) => ({
        ...prev,
        internalTransferSide: initialInternalSide,
      }));
    }
  }, [initialInternalSide, entry.internalTransferSide]);

  // Removed auto equal distribution based on selected members; use Split equally button instead

  useEffect(() => {
    if (entry.sourceType !== JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN) {
      setRemainingAmount(
        Math.abs(entry.amount) -
        Object.values(entry.memberValues).reduce((sum, val) => sum + Math.abs(val), 0),
      );
    }
  }, [entry.amount, entry.sourceType, entry.memberValues]);

  // Member selection is no longer used when always-visible rows are shown

  const recalcFromRations = (
    prev: JournalEntryState,
    override?: {
      memberValues?: Record<number, number>;
      memberRations?: Record<number, number | undefined>;
    },
  ): JournalEntryState => {
    const { amount } = prev;
    const sign = amount < 0 ? -1 : 1;
    const totalAbs = Math.abs(amount);
    const memberValues = override?.memberValues ?? prev.memberValues ?? {};
    const memberRations = override?.memberRations ?? prev.memberRations ?? {};

    const manualIds = Object.keys(memberValues)
      .map((id) => +id)
      .filter((id) => memberRations[id] === undefined);
    const manualAbsSum = manualIds.reduce((sum, id) => sum + Math.abs(memberValues[id] || 0), 0);
    const remainingAbs = Math.max(0, totalAbs - manualAbsSum);

    const rationEntries = Object.entries(memberRations)
      .map(([k, v]) => ({ id: +k, r: v }))
      .filter((e) => e.r !== undefined);
    const sumRations = rationEntries.reduce((sum, e) => sum + (e.r || 0), 0);

    const newMemberValues: Record<number, number> = { ...memberValues };

    if (sumRations > 0 && remainingAbs > 0) {
      let allocatedAbs = 0;
      rationEntries.forEach((e) => {
        const r = e.r || 0;
        if (r <= 0) {
          newMemberValues[e.id] = 0;
        } else {
          const shareAbs = Math.floor((remainingAbs * r) / sumRations);
          newMemberValues[e.id] = sign * shareAbs;
          allocatedAbs += shareAbs;
        }
      });
      let remainder = remainingAbs - allocatedAbs;
      rationEntries.some((e) => {
        if (remainder <= 0) return true;
        if ((e.r || 0) > 0) {
          newMemberValues[e.id] = (newMemberValues[e.id] || 0) + sign * 1;
          remainder -= 1;
        }
        return false;
      });
    } else {
      rationEntries.forEach((e) => {
        if ((e.r || 0) === 0) newMemberValues[e.id] = 0;
      });
    }

    return {
      ...prev,
      memberValues: newMemberValues,
      memberRations,
    };
  };

  const handleValueChange = (memberId: number, value: number) => {
    const totalAmount = entry.amount;
    const isNegative = totalAmount < 0;
    const sign = isNegative ? -1 : 1;

    setEntry((prev) => {
      const inputCentsAbs = Math.max(0, Math.floor(value * 100));
      const memberRations = { ...(prev.memberRations ?? {}) };
      memberRations[memberId] = undefined;

      const pre = recalcFromRations(prev, { memberRations });
      const otherMembersTotalAbs = Object.entries(pre.memberValues)
        .filter(([id]) => +id !== memberId)
        .reduce((sum, [, v]) => sum + Math.abs(v || 0), 0);
      const maxForThisMemberAbs = Math.max(0, Math.abs(totalAmount) - otherMembersTotalAbs);
      const clampedAbs = Math.min(maxForThisMemberAbs, inputCentsAbs);

      const updatedMemberValues = {
        ...pre.memberValues,
        [memberId]: sign * clampedAbs,
      };

      const finalState = recalcFromRations(
        { ...pre, memberValues: updatedMemberValues },
        {
          memberValues: updatedMemberValues,
          memberRations,
        },
      );
      return finalState;
    });
  };

  const handleProcessTypeChange = (value: ProcessType) => {
    setEntry((prev) => ({
      ...prev,
      processType: value,
    }));

    // Initialize split entries when Split is selected (not when we have initialSplitTransactions)
    if (value === 'SPLIT' && !initialSplitJournalEntries?.length) {
      initializeSplitJournalEntries();
    }
  };

  const handleAssignmentTypeChange = (value: AssignmentType) => {
    setEntry((prev) => ({
      ...prev,
      assignmentType: value,
    }));

    // Determine source type based on assignment type and amount
    const isExpense = entry.amount < 0;
    let newSourceType: JournalEntrySourceType;

    if (value === 'ENTIRE_GROUP') {
      newSourceType = isExpense ? JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE : JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME;
    } else {
      // INDIVIDUALS
      newSourceType = isExpense
        ? JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE
        : JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME;
    }

    setEntry((prev) => ({
      ...prev,
      sourceType: newSourceType,
    }));
  };


  const handleInternalSave = () => {
    // Block reassign when editing an existing internal transfer
    if (initialJournalEntry?.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.INTERNAL_TRANSFER) {
      return;
    }
    if (!entry.internalSelectedCounterpartId || !entry.mutationId) {
      toast.error(t('error.internalCandidates'));
      return;
    }
    const currentIsExpense = (entry.amount ?? 0) < 0;
    const fromMutationId = currentIsExpense
      ? entry.mutationId!
      : entry.internalSelectedCounterpartId;
    const toMutationId = currentIsExpense
      ? entry.internalSelectedCounterpartId
      : entry.mutationId!;
    onInternalCreate?.({ fromMutationId, toMutationId, description: entry.description });
  };

  // Note: internal side selection no longer used in UI

  // Load internal transfer candidates when switching to INTERNAL mode and preselect linked counterpart if editing
  useEffect(() => {
    if (entry.processType === 'INTERNAL' && mutation?.mutationId) {
      bankAccountService.getAllBankAccounts().then((acs) => setBankAccountsCount(acs.length));
      bankAccountService
        .getInternalTransferCandidates(mutation.mutationId)
        .then(async (cands) => {
          let preselected: number | undefined;

          // Note: Internal transfers now use journal entries with INTERNAL_TRANSFER source type
          // Legacy transaction ID resolution removed

          if ((cands?.length || 0) === 1) {
            preselected = cands![0].mutationId;
          }

          setEntry((prev) => ({
            ...prev,
            internalCandidates: cands ?? [],
            internalSelectedCounterpartId: preselected,
          }));
        })
        .catch(() => toast.error(t('error.internalCandidates')));
    }
  }, [entry.processType, mutation?.mutationId]);

  // Get transaction info from mutation or initial transaction
  const getJournalEntryInfo = () => {
    if (mutation) {
      return {
        amount: mutation.amount,
        date: new Date(mutation.transactionDate),
        description: mutation.description,
      };
    }
    if (initialJournalEntry) {
      if ('journalEntryId' in initialJournalEntry) {
        const entryTmp = initialJournalEntry as JournalEntryModel;
        return {
          amount: calculateDisplayAmount(entryTmp),
          date: new Date(entryTmp.entryDate),
          description: entryTmp.description,
        };
      }

      const model = initialJournalEntry as JournalEntryModel;
      return {
        amount: calculateDisplayAmount(model),
        date: new Date(model.entryDate),
        description: model.description,
      };
    }
    return { amount: 0, date: new Date(), description: '' };
  };

  // Split functionality handlers
  const initializeSplitJournalEntries = () => {
    // Get values from mutation or entry
    const journalEntryAmount =
      mutation?.amount ??
      (initialJournalEntry ? calculateDisplayAmount(initialJournalEntry as JournalEntryModel) : 0);
    const journalEntryDate = mutation ? new Date(mutation.transactionDate) : (initialJournalEntry ? new Date((initialJournalEntry as JournalEntryModel).entryDate) : new Date());
    const journalEntryDescription = mutation?.description ?? (initialJournalEntry ? (initialJournalEntry as JournalEntryModel).description : '');
    
    const sourceType =
      journalEntryAmount >= 0 ? JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME : JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE;

    const splitJournalEntries: SplitRow[] = [
      {
        mutationId: entry.mutationId,
        sourceType,
        entryDate: journalEntryDate,
        description: `${journalEntryDescription} #1`,
        dashboardId: dashboardId!,
        lines: [],
      },
      {
        mutationId: entry.mutationId,
        sourceType,
        entryDate: journalEntryDate,
        description: `${journalEntryDescription} #2`,
        dashboardId: dashboardId!,
        lines: [],
      },
    ];

    setEntry((prev) => ({
      ...prev,
      splitJournalEntries,
      splitStep: 'details',
    }));
  };

  const addSplitTransaction = () => {
    if (!entry.splitJournalEntries) return;

    // Get values from mutation or entry
    const journalEntryAmount =
      mutation?.amount ??
      (initialJournalEntry ? calculateDisplayAmount(initialJournalEntry as JournalEntryModel) : 0);
    const journalEntryDate = mutation ? new Date(mutation.transactionDate) : (initialJournalEntry ? new Date((initialJournalEntry as JournalEntryModel).entryDate) : new Date());
    const journalEntryDescription = mutation?.description ?? (initialJournalEntry ? (initialJournalEntry as JournalEntryModel).description : '');
    
    const sourceType =
      journalEntryAmount >= 0 ? JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME : JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE;
    const newEntry: SplitRow = {
      mutationId: entry.mutationId,
      sourceType,
      entryDate: journalEntryDate,
      description: `${journalEntryDescription} #${entry.splitJournalEntries.length + 1}`,
      dashboardId: dashboardId!,
      lines: [],
    };

    setEntry((prev) => ({
      ...prev,
      splitJournalEntries: [...(prev.splitJournalEntries || []), newEntry],
    }));
  };

  const removeSplitTransaction = (index: number) => {
    if (!entry.splitJournalEntries || entry.splitJournalEntries.length <= 2) return;

    // Get description from mutation or entry
    const journalEntryDescription = mutation?.description ?? (initialJournalEntry ? (initialJournalEntry as JournalEntryModel).description : '');

    setEntry((prev) => ({
      ...prev,
      splitJournalEntries: prev.splitJournalEntries
        ?.filter((item, idx) => idx !== index)
        .map((split, idx) => ({
          ...split,
          description: `${journalEntryDescription} #${idx + 1}`,
        })),
    }));
  };

  const handleSplitTransactionChange = (
    index: number,
    field: 'description' | 'amount',
    value: string | number,
  ) => {
    setEntry((prev) => {
      if (!prev.splitJournalEntries) return prev;

      const newSplitJournalEntries = [...prev.splitJournalEntries];
      if (field === 'amount') {
        // For amount, we need to update the lines
        // This is simplified - in a full implementation, we'd need to update debit/credit lines
        // Note: Amount changes would require updating journal entry lines with proper ledger accounts
        // For now, we'll need to rebuild lines when saving
        const journalEntryAmount =
          mutation?.amount ??
          (initialJournalEntry ? calculateDisplayAmount(initialJournalEntry as JournalEntryModel) : 0);
        const absAmount = Math.abs((value as number) * 100);
        const isExpense = journalEntryAmount < 0;
        // Create basic lines structure - backend will handle ledger account assignment
        newSplitJournalEntries[index] = {
          ...newSplitJournalEntries[index],
          lines: isExpense 
            ? [{ ledgerAccountId: 0, debit: absAmount, credit: 0, currency: Currency.EUR }]
            : [{ ledgerAccountId: 0, debit: 0, credit: absAmount, currency: Currency.EUR }],
        } as any;
      } else {
        newSplitJournalEntries[index] = {
          ...newSplitJournalEntries[index],
          description: value as string,
        };
      }

      return {
        ...prev,
        splitJournalEntries: newSplitJournalEntries,
      };
    });
  };

  const handleSplitSave = () => {
    if (!entry.splitJournalEntries) return;

    // Pass split entries to parent for processing
    onSplitSuccess?.(entry.splitJournalEntries);
  };

  const handleSave = () => {
    // Derive participating members from non-zero member values
    const participatingMembers = Object.entries(entry.memberValues)
      .filter(([, value]) => (value || 0) !== 0)
      .map(([id]) => +id);

    // TODO call onSplitError
    if (!entry.sourceType || Number.isNaN(entry.amount)) return;
    // Note: Contribution runs are now handled automatically, so we don't validate contribution payment fields here

    // Prevent invalid personal entries without participations
    if (
      (entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE ||
        entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME) &&
      participatingMembers.length === 0
    ) {
      return;
    }

    // For GROUP_EXPENSE and GROUP_INCOME with mutationId, backend will auto-generate lines
    // For personal transactions or when mutationId is not available, we need to build lines manually
    let lines: CreateJournalEntryLineDto[] = [];
    
    const hasMutationId = entry.mutationId !== undefined;
    const isGroupTransaction = entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE || 
                               entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME;
    
    // If we have a mutationId and it's a group transaction, backend will handle lines automatically
    // Otherwise, we need to build lines manually
    if (!hasMutationId || !isGroupTransaction || entry.assignmentType === 'INDIVIDUALS') {
      // Find appropriate ledger accounts
      const walletAccount = ledgerAccounts.find(
        (acc: LedgerAccount) => acc.accountType === LedgerAccountType.ASSET && acc.dashboardMemberId === null
      );
      const expenseAccount = ledgerAccounts.find(
        (acc: LedgerAccount) => acc.accountType === LedgerAccountType.EXPENSE
      );
      const incomeAccount = ledgerAccounts.find(
        (acc: LedgerAccount) => acc.accountType === LedgerAccountType.REVENUE
      );

      // Build journal entry lines from member values with ledger account IDs
      lines = buildJournalEntryLines(
        entry.amount,
        entry.sourceType!,
        Currency.EUR,
        expenseAccount?.ledgerAccountId,
        incomeAccount?.ledgerAccountId,
        walletAccount?.ledgerAccountId,
      );
      
      // Add participant member IDs to lines if this is a personal transaction
      if (entry.assignmentType === 'INDIVIDUALS' && entry.sourceType !== JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && participatingMembers.length > 0) {
        // For personal transactions, create separate lines per member with proper ledger accounts
        const personalLines: CreateJournalEntryLineDto[] = [];
        const isExpense = entry.amount < 0;
        
        participatingMembers.forEach((memberId) => {
          const memberAmount = entry.memberValues[memberId] || 0;
        if (memberAmount !== 0) {
          const absMemberAmount = Math.abs(memberAmount);
          
          // Create expense/income line for this member
          if (isExpense && expenseAccount) {
            personalLines.push({
              ledgerAccountId: expenseAccount.ledgerAccountId,
              participantMemberId: memberId,
              debit: absMemberAmount,
              credit: 0,
              currency: Currency.EUR,
            });
          } else if (!isExpense && incomeAccount) {
            personalLines.push({
              ledgerAccountId: incomeAccount.ledgerAccountId,
              participantMemberId: memberId,
              debit: 0,
              credit: absMemberAmount,
              currency: Currency.EUR,
            });
          }
          
          // Create wallet line for this member
          if (walletAccount) {
            personalLines.push({
              ledgerAccountId: walletAccount.ledgerAccountId,
              participantMemberId: memberId,
              debit: isExpense ? 0 : absMemberAmount,
              credit: isExpense ? absMemberAmount : 0,
              currency: Currency.EUR,
            });
          }
        }
      });
      
      // Replace the generic lines with personal lines if we created any
      if (personalLines.length > 0) {
        // Clear the original lines and use personal lines
        lines.length = 0;
        lines.push(...personalLines);
      }
      }
    }

    // Create journal entry DTO
    const entryDate = entry.entryDate instanceof Date ? entry.entryDate : new Date(entry.entryDate);
    const isPersonalTransaction = entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_EXPENSE ||
                                   entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.PERSONAL_INCOME;
    
    // Build participations array for personal transactions
    // Backend requires participations when lines are not provided or empty
    let participations: BankTransactionParticipation[] | undefined;
    if (isPersonalTransaction && participatingMembers.length > 0) {
      // Always provide participations for personal transactions when we have member values
      // This is especially important when mutationId is present and backend auto-generates lines
      participations = participatingMembers
        .filter((memberId) => {
          const memberAmount = entry.memberValues[memberId] || 0;
          return memberAmount !== 0;
        })
        .map((memberId) => ({
          dashboardMemberId: memberId,
          amount: entry.memberValues[memberId] || 0,
        }));
      
      // If participations array is empty, set to undefined
      if (participations.length === 0) {
        participations = undefined;
      }
    }
    
    const journalEntryDto: CreateJournalEntryDto = {
      dashboardId: dashboardId!,
      description: entry.description,
      entryDate,
      sourceType: entry.sourceType!,
      // Preserve the original transaction amount (from mutation or existing entry)
      amount: journalEntryAmount,
      currency: Currency.EUR,
      mutationId: entry.mutationId,
      payerId: entry.payerId,
      lines,
      // Include participations for personal transactions when lines are not provided
      ...(participations && participations.length > 0 ? { participations } : {}),
      // Set contribution payment metadata if applicable
      ...(entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && entry.contributionPayment
        ? {
            dashboardMemberId: entry.contributionPayment.dashboardMemberId,
            sourceId: entry.contributionPayment.contributionPlanId,
          }
        : {}),
    };

    onSplitSuccess?.(journalEntryDto);
  };

  // Amount formatting handled within SplitMembersList

  const getSplitRemainingAmount = () => {
    if (!entry.splitJournalEntries) return 0;

    const totalSplitAmount = entry.splitJournalEntries.reduce((sum, splitEntry) => {
      if (splitEntry.lines) {
        const fakeEntry = {
          sourceType: entry.sourceType ?? JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE,
          lines: splitEntry.lines,
        } as unknown as JournalEntryModel;
        return sum + calculateDisplayAmount(fakeEntry);
      }
      return sum;
    }, 0);
    // Use entry.amount (the mutation/target we set when editing a split)
    const journalEntryAmount =
      mutation?.amount ??
      (initialJournalEntry ? calculateDisplayAmount(initialJournalEntry as JournalEntryModel) : 0);
    const splitTarget =
      entry.processType === 'SPLIT' ? entry.amount : journalEntryAmount;
    return splitTarget - totalSplitAmount;
  };

  // Select-all is replaced by Split/Reset actions

  // Split equally among selected (or all) members
  const splitEqually = () => {
    setEntry((prevEntry) => {
      const selected = prevEntry.selectedMembers.length
        ? prevEntry.selectedMembers
        : connections.map((c) => +c.dashboardMemberId);

      if (selected.length === 0) return prevEntry;

      const total = prevEntry.amount;
      const sign = total < 0 ? -1 : 1;
      const totalAbs = Math.abs(total);
      const equalAbs = Math.floor(totalAbs / selected.length);
      const remainder = totalAbs % selected.length;

      const memberValues: Record<number, number> = {};
      const memberRations: Record<number, number | undefined> = {};
      selected.forEach((id, index) => {
        memberValues[id] = sign * (equalAbs + (index < remainder ? 1 : 0));
        memberRations[id] = undefined;
      });

      return {
        ...prevEntry,
        selectedMembers: selected,
        memberValues,
        memberRations,
      };
    });
  };

  // Clear all selections and values
  const resetSplit = () => {
    setEntry((prevEntry) => ({
      ...prevEntry,
      selectedMembers: [],
      memberValues: {},
      memberRations: {},
    }));
  };

  const isResetable = useMemo(
    () => Object.values(entry.memberValues).some((v) => Math.abs(v || 0) > 0),
    [entry.memberValues],
  );

  if (!entry) {
    return null;
  }

  const journalEntryInfo = getJournalEntryInfo();
  const journalEntryAmount = journalEntryInfo.amount;
  const journalEntryDate = journalEntryInfo.date;
  const journalEntryDescription = journalEntryInfo.description;

  // Handle delete
  const handleDelete = () => {
    if (!initialJournalEntry) return;
    
    if ('journalEntryId' in initialJournalEntry) {
      // Delete journal entry
      if (window.confirm(t('confirmDelete') || 'Are you sure you want to delete this journal entry?')) {
        bankAccountService
          .deleteJournalEntry((initialJournalEntry as JournalEntryModel).journalEntryId)
          .then(() => {
            toast.success(t('deleteSuccess') || 'Journal entry deleted successfully');
            setOpen(false);
            onSplitSuccess?.([]);
          })
          .catch((error) => {
            toast.error(t('deleteError') || `Failed to delete: ${error.message}`);
          });
      }
    } 
  };

  // Get assigned members info for view mode
  const getAssignedInfo = () => {
    if (!initialJournalEntry) return null;
    
    // Handle journal entry
    if ('journalEntryId' in initialJournalEntry) {
      const model = initialJournalEntry as JournalEntryModel;
      
      // Contribution payment
      if (model.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && model.dashboardMemberId) {
        const member = connections.find((c) => +c.dashboardMemberId === model.dashboardMemberId);
        const plan = model.sourceId
          ? Object.values(plans).find((p) => p.contributionPlanId === model.sourceId)
          : null;
        return {
          type: 'contribution',
          member,
          plan: plan?.name,
        };
      }
      
      // Participations from lines
      const participantLines = model.lines?.filter((l) => l.participantMemberId) || [];
      if (participantLines.length > 0) {
        const memberIds = participantLines.map((l) => l.participantMemberId!);
        const members = connections.filter((c) =>
          memberIds.some((id) => +c.dashboardMemberId === id),
        );
        return {
          type: 'individuals',
          members,
        };
      }
      
      // Group transaction
      if (
        entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_INCOME ||
        entry.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE
      ) {
        return {
          type: 'group',
        };
      }
      
      return null;
    }
    

    // const journalEntry = initialJournalEntry as JournalEntryModel;

    // // Contribution payment
    // if (journalEntry.contributionPayment?.dashboardMemberId) {
    //   const member = connections.find(
    //     (c) => +c.dashboardMemberId === journalEntry.contributionPayment!.dashboardMemberId,
    //   );
    //   const plan = journalEntry.contributionPayment.contributionPlanId
    //     ? Object.values(plans).find((p) => p.contributionPlanId === tx.contributionPayment!.contributionPlanId)
    //     : null;
    //   return {
    //     type: 'contribution',
    //     member,
    //     plan: plan?.name,
    //   };
    // }

    // // Participations
    // if (tx.participations && tx.participations.length > 0) {
    //   const memberIds = tx.participations.map((p) => p.dashboardMemberId);
    //   const members = connections.filter((c) =>
    //     memberIds.some((id) => +c.dashboardMemberId === id),
    //   );
    //   return {
    //     type: 'individuals',
    //     members,
    //   };
    // }

    // // Group transaction
    // if (
    //   tx.transactionType === TransactionType.GROUP_INCOME ||
    //   tx.transactionType === TransactionType.GROUP_EXPENSE
    // ) {
    //   return {
    //     type: 'group',
    //   };
    // }

    return null;
  };

  const assignedInfo = getAssignedInfo();

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl rounded-lg" closeButton>
      <div className="relative flex max-h-[calc(100vh-5rem)] flex-col overflow-hidden rounded-md bg-secondary-50">
        <div className="flex-1 overflow-y-auto p-4">
          {isViewMode ? (
            <>
              {/* View Mode Header */}
              <div className="mb-4 flex items-center gap-2">
                <h3 className="text-lg font-semibold">
                  {t('transactionDetails') || 'Transaction Details'}
                </h3>
                {initialJournalEntry && initialJournalEntry.sourceType && (
                  <TypeBadge sourceType={initialJournalEntry.sourceType} />
                )}
              </div>

              {/* Transaction Info Card - Compact */}
              <div className="mb-4 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500">{t('date')}</p>
                    <p className="text-sm font-semibold">
                      {formatDate(
                        initialJournalEntry
                          ? new Date(initialJournalEntry.entryDate)
                          : journalEntryDate
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">{t('amount')}</p>
                    <p
                      className={twMerge(
                        'text-sm font-semibold',
                        journalEntryAmount > 0 ? 'text-success' : 'text-danger',
                      )}>
                      {formatEuroCentsWithSign(journalEntryAmount, i18n.language)}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500">{t('description')}</p>
                  <p className="max-w-md truncate text-sm font-semibold" title={journalEntryDescription}>
                    {journalEntryDescription}
                  </p>
                </div>

                {/* Assignment Information */}
                {assignedInfo && (
                  <div className="border-t border-gray-200 pt-3">
                    <p className="mb-2 text-xs font-medium text-gray-500">
                      {t('assignedTo') || 'Assigned To'}
                    </p>
                    {assignedInfo.type === 'contribution' && assignedInfo.member && (
                      <div className="flex items-center gap-2">
                        <Avatar
                          className="h-6 w-6 rounded-full"
                          src={assignedInfo.member.picture}
                          alias={`${assignedInfo.member.fields.FIRST_NAME}+${assignedInfo.member.fields.LAST_NAME}`}
                        />
                        <span className="text-sm font-medium">
                          {assignedInfo.member.fields.FIRST_NAME} {assignedInfo.member.fields.LAST_NAME}
                        </span>
                        {assignedInfo.plan && (
                          <span className="text-xs text-primary-300">• {assignedInfo.plan}</span>
                        )}
                      </div>
                    )}
                    {assignedInfo.type === 'individuals' && assignedInfo.members && (
                      <div className="flex flex-wrap items-center gap-2">
                        {assignedInfo.members.map((member) => (
                          <div key={member.dashboardMemberId} className="flex items-center gap-1">
                            <Avatar
                              className="h-6 w-6 rounded-full"
                              src={member.picture}
                              alias={`${member.fields.FIRST_NAME}+${member.fields.LAST_NAME}`}
                            />
                            <span className="text-sm">
                              {member.fields.FIRST_NAME} {member.fields.LAST_NAME}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {assignedInfo.type === 'group' && (
                      <span className="text-sm font-medium">{community?.name || 'Group'}</span>
                    )}
                  </div>
                )}

                {/* Processor Information */}
                {initialJournalEntry && (
                  <div className="border-t border-gray-200 pt-3">
                    <p className="mb-2 text-xs font-medium text-gray-500">{t('processedBy')}</p>
                    <TransactionProcessor entry={initialJournalEntry} />
                  </div>
                )}
              </div>
            </div>
            </>
          ) : (
            <>
              {/* Edit Mode Header */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold">
                  {editMode ? t('editTransaction') : t('addTransaction')}
                </h3>
              </div>

              {/* Transaction Info Card - Same style as view mode */}
              <div className="mb-6 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500">{t('date')}</p>
                      <p className="text-sm font-semibold">{formatDate(journalEntryDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500">{t('amount')}</p>
                      <p
                        className={twMerge(
                          'text-sm font-semibold',
                          journalEntryAmount > 0 ? 'text-success' : 'text-danger',
                        )}>
                        {formatEuroCentsWithSign(journalEntryAmount, i18n.language)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">{t('description')}</p>
                    <p className="max-w-md truncate text-sm font-semibold" title={journalEntryDescription}>
                      {journalEntryDescription}
                    </p>
                  </div>

                  {/* Assignment Information - Show if editing existing transaction */}
                  {assignedInfo && (
                    <div className="border-t border-gray-200 pt-3">
                      <p className="mb-2 text-xs font-medium text-gray-500">
                        {t('assignedTo') || 'Assigned To'}
                      </p>
                      {assignedInfo.type === 'contribution' && assignedInfo.member && (
                        <div className="flex items-center gap-2">
                          <Avatar
                            className="h-6 w-6 rounded-full"
                            src={assignedInfo.member.picture}
                            alias={`${assignedInfo.member.fields.FIRST_NAME}+${assignedInfo.member.fields.LAST_NAME}`}
                          />
                          <span className="text-sm font-medium">
                            {assignedInfo.member.fields.FIRST_NAME} {assignedInfo.member.fields.LAST_NAME}
                          </span>
                          {assignedInfo.plan && (
                            <span className="text-xs text-primary-300">• {assignedInfo.plan}</span>
                          )}
                        </div>
                      )}
                      {assignedInfo.type === 'individuals' && assignedInfo.members && (
                        <div className="flex flex-wrap items-center gap-2">
                          {assignedInfo.members.map((member) => (
                            <div key={member.dashboardMemberId} className="flex items-center gap-1">
                              <Avatar
                                className="h-6 w-6 rounded-full"
                                src={member.picture}
                                alias={`${member.fields.FIRST_NAME}+${member.fields.LAST_NAME}`}
                              />
                              <span className="text-sm">
                                {member.fields.FIRST_NAME} {member.fields.LAST_NAME}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      {assignedInfo.type === 'group' && (
                        <span className="text-sm font-medium">{community?.name || 'Group'}</span>
                      )}
                    </div>
                  )}

                  {/* Processor Information */}
                  {initialJournalEntry && (
                    <div className="border-t border-gray-200 pt-3">
                      <p className="mb-2 text-xs font-medium text-gray-500">{t('processedBy')}</p>
                        <TransactionProcessor entry={initialJournalEntry as JournalEntryModel} />
                    </div>
                  )}
                </div>
              </div>

            {/* Internal transfer badge and counterpart navigation removed */}

            {/* Process Type Slider */}
            <div className="mb-6 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
              <Slider
                label={t('processLabel')}
                items={[
                  { label: t('processAssign'), value: 'ASSIGN' as ProcessType },
                  { label: t('processSplit'), value: 'SPLIT' as ProcessType },
                  // Only show INTERNAL option when there are 2+ bank accounts
                  ...(bankAccountsCount !== undefined && bankAccountsCount > 1
                    ? [{ label: t('processInternal'), value: 'INTERNAL' as ProcessType }]
                    : []),
                ]}
                value={entry.processType}
                onChange={handleProcessTypeChange}
              />
            </div>

            {/* Split Functionality */}
            {entry.processType === 'SPLIT' && (
              <div className="mb-6">
                {/* Transaction details */}
                {entry.splitJournalEntries && (
                  <div className="space-y-4 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
                    <div className="mb-4">
                      <h4 className="mb-2 text-base font-semibold">{t('transactionViewSubtitle')}</h4>
                      <p className="text-sm text-gray-600">
                        Fill in the details for each transaction. The total amount must equal the
                        original mutation amount.
                      </p>
                    </div>

                    <div className="space-y-3">
                      {entry.splitJournalEntries.map((splitJournalEntry, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-3 transition-colors hover:border-secondary-300">
                          <div className="flex-shrink-0">
                            <span className="text-sm font-semibold text-gray-700">
                              {t('transactionN', { number: index + 1 })}
                            </span>
                          </div>

                          <div className="flex-1">
                            <input
                              type="text"
                              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                              placeholder={t('descriptionPlaceholder')}
                              value={splitJournalEntry.description || ''}
                              onChange={(e) =>
                                handleSplitTransactionChange(index, 'description', e.target.value)
                              }
                            />
                          </div>

                          <div className="w-32">
                            <CurrencyInput
                              type="text"
                              className="w-full"
                              handleChange={(value) =>
                                handleSplitTransactionChange(index, 'amount', value)
                              }
                              value={
                                splitJournalEntry.lines
                                  ? `€${(
                                      calculateDisplayAmount(
                                        {
                                          sourceType:
                                            entry.sourceType ??
                                            JOURNAL_ENTRY_SOURCE_TYPES.GROUP_EXPENSE,
                                          lines: splitJournalEntry.lines,
                                        } as unknown as JournalEntryModel,
                                      ) / 100
                                    ).toFixed(2)}`
                                  : undefined
                              }
                              debounced
                            />
                          </div>

                          {/* Remove button - only show if more than 2 transactions */}
                          {entry.splitJournalEntries &&
                            entry.splitJournalEntries.length > 2 && (
                              <button
                                type="button"
                                onClick={() => removeSplitTransaction(index)}
                                className="flex-shrink-0 rounded-md p-2 text-danger transition-colors hover:bg-red-50 hover:text-red-700"
                                title="Remove transaction">
                                <svg
                                  className="h-5 w-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            )}
                        </div>
                      ))}

                      {/* Add transaction button */}
                      <div className="flex justify-center pt-2">
                        <Button
                          onClick={addSplitTransaction}
                          variant="primary-dark"
                          className="hover:bg-secondary-300 bg-secondary-200 text-primary-900">
                          {t('addAnotherTransaction')}
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between rounded-md border border-secondary-200 bg-secondary-50 p-3">
                      <span className="text-sm font-semibold text-gray-700">{t('remaining')}:</span>
                      <span
                        className={`text-sm font-semibold ${getSplitRemainingAmount() === 0 ? 'text-success' : 'text-danger'}`}>
                        €{(getSplitRemainingAmount() / 100).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Internal transfer controls */}
            {entry.processType === 'INTERNAL' && (
              <div className="mb-6 rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
                {/* Empty state if only one bank account */}
                {bankAccountsCount !== undefined && bankAccountsCount <= 1 && (
                  <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-3 text-sm text-primary-900">
                    {t('internalNotPossibleSingleAccount')}
                  </div>
                )}

                {/* No candidates message */}
                {bankAccountsCount !== undefined &&
                  bankAccountsCount > 1 &&
                  (entry.internalCandidates?.length || 0) === 0 && (
                    <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-3 text-sm text-primary-900">
                      {t('noInternalCandidates')}
                    </div>
                  )}

                {/* Counterpart transaction display removed - internal transfers now use journal entries with INTERNAL_TRANSFER source type */}

                {/* Description editor */}
                <div className="mb-4">
                  <Labeled label={t('description')} htmlFor="internal-transaction-description-textarea">
                    <textarea
                      className="h-[unset] w-full"
                      rows={2}
                      id="internal-transaction-description-textarea"
                      placeholder={t('descriptionPlaceholder')}
                      value={entry.description}
                      onChange={(ev) =>
                        setEntry((prevEntry) => ({
                          ...prevEntry,
                          description: ev.target.value,
                        }))
                      }
                    />
                  </Labeled>
                </div>
                {/* Exactly one candidate - show summary, no dropdown */}
                {/* {bankAccountsCount !== undefined &&
              bankAccountsCount > 1 &&
              (transaction.internalCandidates?.length || 0) === 1 && (
                <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-3">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    {t('counterpartMutation')}
                  </p>
                  <div className="flex flex-wrap items-center gap-3 text-sm">
                    <span>
                      {formatDate(
                        new Date(
                          transaction.internalCandidates![0].transactionDate as unknown as Date,
                        ),
                      )}
                    </span>
                    <span>
                      {formatEuroCentsWithSign(
                        transaction.internalCandidates![0].amount,
                        i18n.language,
                      )}
                    </span>
                    <span className="text-gray-700">
                      {transaction.internalCandidates![0].description}
                    </span>
                  </div>
                </div>
              )} */}
                {/* Counterpart selection (hidden in edit mode for existing internal transfers). Hide single-candidate summary in edit mode too. */}
                {(() => {
                  const isEditingInternal =
                    initialJournalEntry?.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.INTERNAL_TRANSFER;
                  const candidatesCount = entry.internalCandidates?.length || 0;
                  if (isEditingInternal) return null;
                  if (candidatesCount === 1) {
                    return (
                      <div className="mb-4 rounded-md border border-secondary-200 bg-secondary-50 p-3">
                        <p className="mb-2 text-xs font-semibold text-gray-600">{t('member')}</p>
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                          <span className="text-gray-700">
                            {formatDate(
                              new Date(entry.internalCandidates![0].transactionDate),
                            )}
                          </span>
                          <span className="font-medium text-gray-700">
                            {formatEuroCentsWithSign(
                              entry.internalCandidates![0].amount,
                              i18n.language,
                            )}
                          </span>
                          <span className="text-gray-600">
                            {entry.internalCandidates![0].description}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="mt-4">
                      <Labeled label="Counterpart mutation">
                        <DropdownSelect
                          options={(entry.internalCandidates ?? []).map((m) => ({
                            label: `${formatDate(new Date(m.transactionDate))} • ${m.oppositeName} • ${formatEuroCentsWithSign(m.amount, i18n.language)}`,
                            value: m.mutationId,
                          }))}
                          setValue={(val) =>
                            setEntry((prev) => ({
                              ...prev,
                              internalSelectedCounterpartId: val || undefined,
                            }))
                          }
                          value={entry.internalSelectedCounterpartId}
                        />
                      </Labeled>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Assign Flow */}
            {entry.processType === 'ASSIGN' && (
              <div className="mb-6 space-y-6">
                {/* Description Section */}
                <div className="rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
                  <Labeled label={t('description')} htmlFor="transaction-description-textarea">
                    <textarea
                      className="h-[unset] w-full"
                      rows={2}
                      id="transaction-description-textarea"
                      placeholder={t('descriptionPlaceholder')}
                      value={entry.description}
                      onChange={(ev) =>
                        setEntry((prev) => ({
                          ...prev,
                          description: ev.target.value,
                        }))
                      }
                    />
                  </Labeled>
                </div>

                {/* Transaction Type Selection with Sliders */}
                <div className="rounded-lg border border-secondary-200 bg-white p-4 shadow-sm">
                  {/* First Slider - Assignment Type */}
                  <div className="mb-4">
                    <Slider
                      label={t('assignmentTypeLabel', {
                        type:
                          entry.amount < 0
                            ? t('expense').toLocaleLowerCase()
                            : t('income').toLocaleLowerCase(),
                      })}
                      items={[
                        { label: t('entireGroup'), value: 'ENTIRE_GROUP' as AssignmentType },
                        { label: t('individuals'), value: 'INDIVIDUALS' as AssignmentType },
                      ]}
                      value={entry.assignmentType}
                      onChange={handleAssignmentTypeChange}
                    />
                  </div>

                </div>


                {entry.assignmentType === 'INDIVIDUALS' &&
                  entry.sourceType !== JOURNAL_ENTRY_SOURCE_TYPES.CONTRIBUTION_RUN && (
                    <SplitMembersList
                      members={connections.map((m) => ({
                        id: +m.dashboardMemberId,
                        label: `${m.fields.FIRST_NAME} ${m.fields.LAST_NAME}`,
                      }))}
                      memberValues={entry.memberValues}
                      memberRations={entry.memberRations}
                      onSetValue={(id, v) => handleValueChange(id, v)}
                      onSetRations={(id, r) =>
                        setEntry((prev) =>
                          recalcFromRations(prev, {
                            memberRations: { ...(prev.memberRations ?? {}), [id]: r },
                          }),
                        )
                      }
                      headerTitle={t('selectMembers')}
                      onSplit={splitEqually}
                      onClear={resetSplit}
                      isResetable={isResetable}
                      remainingAmount={remainingAmount}
                      remainingLabel={t('remaining')}
                    />
                  )}
              </div>
            )}
            </>
          )}
        </div>

        {/* Sticky Footer with Action Buttons */}
        <div className="sticky bottom-0 z-10 border-t border-secondary-200 bg-secondary-50 p-4 shadow-lg">
          {isViewMode ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => setIsViewMode(false)}
                className="flex items-center gap-2">
                <TbEdit className="h-4 w-4" />
                {t('edit') || 'Edit'}
              </Button>
              <Button variant="danger" onClick={handleDelete} className="flex items-center gap-2">
                <TbTrash className="h-4 w-4" />
                {t('delete') || 'Delete'}
              </Button>
            </div>
          ) : (
            <div className="flex justify-end gap-2">
              <Button
                variant="primary-dark"
                className="bg-secondary-200 text-primary-900"
                onClick={() => {
                  if (isExistingEntry) {
                    setIsViewMode(true);
                  }
                  setOpen(false);
                }}>
                {t('cancelButton')}
              </Button>

              {/* Split save button */}
              {entry.processType === 'SPLIT' && entry.splitJournalEntries && (
                <Button
                  variant="primary-dark"
                  onClick={handleSplitSave}
                  disabled={
                    !entry.splitJournalEntries ||
                    entry.splitJournalEntries.some(
                      (splitEntry) => !splitEntry.description || !splitEntry.lines || splitEntry.lines.length === 0,
                    ) ||
                    getSplitRemainingAmount() !== 0
                  }>
                  {t('confirmSplit')}
                </Button>
              )}

              {/* Assign save button */}
              {entry.processType === 'ASSIGN' && (
                <Button
                  variant="primary-dark"
                  onClick={handleSave}
                  disabled={
                    !entry.sourceType ||
                    !entry.amount ||
                    (entry.assignmentType === 'INDIVIDUALS' && remainingAmount !== 0)
                  }>
                  {t('assignButton')}
                </Button>
              )}

              {/* Internal save button */}
              {entry.processType === 'INTERNAL' && (
                <Button
                  variant="primary-dark"
                  onClick={handleInternalSave}
                  disabled={
                    initialJournalEntry?.sourceType === JOURNAL_ENTRY_SOURCE_TYPES.INTERNAL_TRANSFER ||
                    !entry.internalSelectedCounterpartId
                  }>
                  {t('assignButton')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </ModalLayout>
  );
}